import React from 'react';
import { Modal, Table, Spinner, Alert, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'redux/store';
import { selectHour12Option } from 'redux/selectors/userSettings';
import { OpenPeriod } from './types';
import { formatPeriodDatetime, calculateDuration } from './helpers';

interface OpeningMomentsModalProps {
  show: boolean;
  onHide: () => void;
  periods: OpenPeriod[] | readonly OpenPeriod[];
  isLoading: boolean;
  error: string | null;
  windowName: string;
  timezone?: string;
  /**
   * When the periods were asked for. Openings are "from now on", so a list with no
   * instant beside it cannot be judged — it may have been computed a while ago.
   */
  asOf?: Date;
  /** Offer a re-evaluate control. Omit where the caller already holds the periods. */
  onRefresh?: () => void;
}

/**
 * A window's open periods as a table. Presentational and prop-driven: the
 * scheduling-window list feeds it from an evaluation query, and the submit page
 * feeds it periods it already has.
 */
const OpeningMomentsModal: React.FC<OpeningMomentsModalProps> = ({ show, onHide, periods, isLoading, error, windowName, timezone, asOf, onRefresh }) => {
  const { t } = useTranslation();
  const hour12 = useAppSelector(selectHour12Option);

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {t('Open Periods')}: {windowName}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading && periods.length === 0 && (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" />
            <div className="mt-2">{t('Evaluating schedule...')}</div>
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {!isLoading && !error && periods.length === 0 && <Alert variant="info">{t('No open periods found in the evaluation range.')}</Alert>}

        {periods.length > 0 && (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>{t('Opens')}</th>
                <th>{t('Closes')}</th>
                <th>{t('Duration')}</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((period, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{formatPeriodDatetime(period.open, timezone, hour12)}</td>
                  <td>{formatPeriodDatetime(period.close, timezone, hour12)}</td>
                  <td>{calculateDuration(period.open, period.close)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>

      {(asOf !== undefined || onRefresh !== undefined) && (
        <Modal.Footer className="justify-content-between">
          <span className="text-muted small">{asOf !== undefined && `${t('Evaluated at')} ${formatPeriodDatetime(asOf.toISOString(), timezone, hour12)}`}</span>
          {onRefresh !== undefined && (
            <Button variant="outline-secondary" size="sm" onClick={onRefresh} disabled={isLoading}>
              <FontAwesomeIcon icon={faRotateRight} className="me-2" />
              {isLoading ? t('Evaluating...') : t('Re-evaluate')}
            </Button>
          )}
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default OpeningMomentsModal;
